'use client'

import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { toast } from 'sonner'

import { REMOVE_WORKSPACE_LOGO, UPDATE_WORKSPACE_LOGO } from '../data/mutations/workspace'
import { GET_WORKSPACE, GET_WORKSPACE_SIGNED_URL } from '../data/queries/workspace'

interface IUploadWorkspaceLogoParams {
  fileObject: Blob
  setError: (error: string) => void
}

export const useUploadWorkspacePicture = (workspaceId: string) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isRemovingLogo, setIsRemovingLogo] = useState(false)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const [updateLogo] = useMutation(UPDATE_WORKSPACE_LOGO, { refetchQueries: [GET_WORKSPACE] })
  const [removeLogo] = useMutation(REMOVE_WORKSPACE_LOGO, { refetchQueries: [GET_WORKSPACE] })

  // Get signed URL for workspace logo upload
  const { refetch: getWorkspaceSignedURL } = useQuery(GET_WORKSPACE_SIGNED_URL, {
    context: {
      headers: {
        'ws-id': workspaceId
      }
    },
    fetchPolicy: 'no-cache',
    skip: true,
    variables: {
      imageType: 'image/jpeg' // Default image type, will be overridden when called
    }
  })

  const handleGetSignedURL = async (fileType: string) => {
    try {
      const { data } = await getWorkspaceSignedURL({
        imageType: fileType
      })

      if (data?.workspace?.url) {
        setSignedUrl(data.workspace.url)
        return data.workspace.url
      }
      return null
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get upload URL. Please try again.'
      toast.error(errorMessage)
      return null
    }
  }

  // Upload file to S3 using signed URL
  const uploadLogoToS3 = async (url: string, file: Blob) => {
    if (!url || !file) {
      throw new Error('URL and file are required')
    }

    try {
      const response = await fetch(url, {
        body: file,
        headers: {
          'Content-Type': file.type
        },
        method: 'PUT'
      })

      if (response.ok) {
        return {
          message: 'File uploaded successfully',
          status: response.status
        }
      }

      throw new Error(`Upload failed with status: ${response.status}`)
    } catch (error) {
      console.error('Error uploading to S3:', error)
      throw error
    }
  }

  const uploadWorkspaceLogo = async ({ fileObject, setError }: IUploadWorkspaceLogoParams) => {
    try {
      setIsLoading(true)

      // Get signed URL with the correct file type
      const url = await handleGetSignedURL(fileObject.type)
      if (!url) {
        throw new Error('Failed to get upload URL')
      }

      // Upload to S3
      const uploadedImageURL = url.split('?')[0]
      const imageUploaded = await uploadLogoToS3(url, fileObject)

      if (imageUploaded?.status === 200) {
        // Update workspace with new logo URL
        await updateLogo({
          context: {
            headers: {
              'ws-id': workspaceId
            }
          },
          variables: {
            workspaceLogoURL: `${uploadedImageURL}?timestamp=${Date.now()}`
          }
        })
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload logo'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const uploadAvatarAsWorkspaceLogo = async (avatarUrl: string) => {
    setIsLoading(true)
    try {
      // For avatars, we can directly use the URL
      await updateLogo({
        context: {
          headers: {
            'ws-id': workspaceId
          }
        },
        variables: {
          workspaceLogoURL: avatarUrl
        }
      })
    } catch (error) {
      console.error('Error uploading avatar as workspace logo:', error)
      toast.error('Failed to set workspace logo. Please try again.')
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const removeWorkspaceLogo = async () => {
    setIsRemovingLogo(true)
    try {
      await removeLogo({
        context: {
          headers: {
            'ws-id': workspaceId
          }
        }
      })
    } catch (error) {
      console.error('Error removing workspace logo:', error)
      toast.error('Failed to remove workspace logo. Please try again.')
      throw error
    } finally {
      setIsRemovingLogo(false)
    }
  }

  return {
    isLoading,
    isRemovingLogo,
    removeWorkspaceLogo,
    signedUrl,
    uploadAvatarAsWorkspaceLogo,
    uploadWorkspaceLogo
  }
}
